import { Clock, ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { formatDateInTimeZone } from '../../utils/timezoneDisplay';

interface TimelineEvent {
  label: string;
  time: string;
  actor?: string | null;
  detail?: string | null;
  color: string;
  dotColor: string;
}

interface AppointmentTimelineSectionProps {
  appointment: any;
  linkedPOs?: any[];
  warehouseTimeZone?: string;
  defaultExpanded?: boolean;
  approvalHistory?: any;
  activityHistory?: any[];
}

export function AppointmentTimelineSection({ appointment, linkedPOs, warehouseTimeZone, defaultExpanded = false, approvalHistory }: AppointmentTimelineSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const shortId = (id: string | null | undefined) => {
    if (!id) return null;
    if (id.includes('@') || id.startsWith('Warehouse:') || id.startsWith('Carrier:')) return id;
    return `User …${id.slice(-5)}`;
  };

  const fmtEventTime = (iso: string) => {
    return formatDateInTimeZone(iso, warehouseTimeZone, true);
  };

  // Build event list from existing timestamps + durable approvalHistory
  const events: TimelineEvent[] = [];

  if (appointment.requestedAt) {
    events.push({ label: 'Request submitted', time: appointment.requestedAt, actor: appointment.carrierName || null, detail: null, color: 'text-slate-500 dark:text-slate-400', dotColor: 'bg-slate-400 dark:bg-slate-500' });
  }

  // Parse dock reassignment events from approvalHistory or fallback to appointment.notes
  const reassignEvents: Array<{ fromDock: string; toDock: string; isoTime: string | null; actor: string | null }> = [];
  if (Array.isArray(approvalHistory?.dockReassignments) && approvalHistory.dockReassignments.length > 0) {
    for (const r of approvalHistory.dockReassignments) {
      const fromLabel = r.previousDockNumber || r.previousDockId || 'Unassigned';
      const toLabel = r.newDockNumber || r.newDockId || 'Assigned';
      reassignEvents.push({
        fromDock: String(fromLabel),
        toDock: String(toLabel),
        isoTime: r.reassignedAt || null,
        actor: r.reassignedByActor || (r.reassignedBy ? shortId(r.reassignedBy) : null),
      });
    }
  } else if (typeof appointment.notes === 'string' && appointment.notes.includes('Dock reassigned:')) {
    const lines = appointment.notes.split('\n');
    for (const line of lines) {
      const m = line.match(/Dock reassigned:\s*Dock\s+([^\s→]+)\s*→\s*Dock\s+([^\s(]+)(?:\s*\(([^)]+)\))?/i);
      if (m) {
        reassignEvents.push({
          fromDock: m[1],
          toDock: m[2],
          isoTime: m[3] || null,
          actor: null,
        });
      }
    }
  }

  const origApproval = approvalHistory?.originalApproval;
  const effectiveApprovedAt = origApproval?.approvedAt || appointment.approvedAt || null;
  const effectiveApprovedBy = origApproval?.approvedByActor || (origApproval?.approvedBy ? shortId(origApproval.approvedBy) : (appointment.approvedBy ? shortId(appointment.approvedBy) : null));
  const initialApprovedDockNumber = origApproval?.dockNumber
    || (reassignEvents.length > 0 ? reassignEvents[0].fromDock : null)
    || appointment.dockNumber
    || appointment.lastAssignedDockNumber
    || null;

  if (effectiveApprovedAt) {
    events.push({
      label: 'Approved',
      time: effectiveApprovedAt,
      actor: effectiveApprovedBy,
      detail: initialApprovedDockNumber ? `Dock ${initialApprovedDockNumber}` : null,
      color: 'text-emerald-600 dark:text-emerald-400',
      dotColor: 'bg-emerald-500',
    });
  }

  for (const r of reassignEvents) {
    const evTime = r.isoTime || appointment.updatedAt || effectiveApprovedAt || appointment.requestedAt;
    if (evTime) {
      events.push({
        label: 'Dock reassigned',
        time: evTime,
        actor: r.actor,
        detail: `Dock ${r.fromDock} → Dock ${r.toDock}`,
        color: 'text-amber-600 dark:text-amber-400',
        dotColor: 'bg-amber-500',
      });
    }
  }

  if (appointment.checkInTime) {
    let checkInDetail: string | null = null;
    if (appointment.wasLate && appointment.lateMinutes) {
      checkInDetail = `Late by ${appointment.lateMinutes} min`;
    } else if (appointment.startAt) {
      const diffMins = Math.floor((new Date(appointment.checkInTime).getTime() - new Date(appointment.startAt).getTime()) / 60000);
      if (diffMins < -60) {
        const absMins = Math.abs(diffMins);
        const earlyLabel = absMins >= 1440
          ? `${Math.floor(absMins / 1440)}d${Math.round((absMins % 1440) / 60) > 0 ? ` ${Math.round((absMins % 1440) / 60)}h` : ''}`
          : absMins >= 60
            ? `${Math.floor(absMins / 60)}h${absMins % 60 > 0 ? ` ${absMins % 60}m` : ''}`
            : `${absMins} min`;
        checkInDetail = `Early by ${earlyLabel}`;
      }
    }
    events.push({ label: 'Checked in', time: appointment.checkInTime, actor: appointment.checkedInBy ? shortId(appointment.checkedInBy) : null, detail: checkInDetail, color: 'text-blue-600 dark:text-blue-400', dotColor: 'bg-blue-500' });
  }

  if (appointment.dockStartTime) {
    events.push({ label: 'Dispatched to dock', time: appointment.dockStartTime, actor: appointment.dispatchedBy ? shortId(appointment.dispatchedBy) : null, detail: appointment.dockNumber ? `Dock ${appointment.dockNumber}` : null, color: 'text-cyan-600 dark:text-cyan-400', dotColor: 'bg-cyan-500' });
  }

  // Receipt events from linked POs
  if (linkedPOs) {
    const receiptPOs = linkedPOs.filter((po: any) => po.receivedAt);
    if (receiptPOs.length > 0) {
      const sortedReceipts = [...receiptPOs].sort((a: any, b: any) => new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime());
      const first = sortedReceipts[0];
      const receiptSummary = receiptPOs.length === 1 ? first.poNumber : `${receiptPOs.length} POs`;
      events.push({ label: 'Receiving recorded', time: first.receivedAt, actor: first.receivedBy ? shortId(first.receivedBy) : null, detail: receiptSummary, color: 'text-indigo-600 dark:text-indigo-400', dotColor: 'bg-indigo-500' });
    }
  }

  if (appointment.checkOutTime) {
    const dwellLabel = appointment.dwellMinutes ? `${appointment.dwellMinutes} min dwell` : null;
    events.push({ label: 'Completed', time: appointment.checkOutTime, actor: appointment.completedBy ? shortId(appointment.completedBy) : null, detail: dwellLabel, color: 'text-emerald-600 dark:text-emerald-400', dotColor: 'bg-emerald-500' });
  }

  if (appointment.status === 'cancelled') {
    const cancelInfo = approvalHistory?.cancellation;
    const rawNotes = String(appointment.notes || appointment.cancelReason || '');
    const lowerNotes = rawNotes.toLowerCase();
    const isCarrierCancelled = cancelInfo?.cancellationOrigin === 'carrier' || lowerNotes.includes('cancelled by carrier');
    const isDeclined = !isCarrierCancelled && (cancelInfo?.cancellationOrigin === 'warehouse_declined' || lowerNotes.startsWith('declined'));
    const cancelLabel = isCarrierCancelled
      ? 'Cancelled by Carrier'
      : isDeclined
        ? 'Declined'
        : 'Cancelled by Warehouse';
    const releasedDock = cancelInfo?.releasedDockNumber || appointment.lastAssignedDockNumber || null;
    const detailParts: string[] = [];
    if (releasedDock) detailParts.push(`Released Dock ${releasedDock}`);
    if (cancelInfo?.reason && !String(cancelInfo.reason).startsWith('Cancelled by') && !String(cancelInfo.reason).startsWith('Declined by')) {
      detailParts.push(`Reason: ${cancelInfo.reason}`);
    }
    events.push({
      label: cancelLabel,
      time: cancelInfo?.cancelledAt || appointment.updatedAt || appointment.requestedAt,
      actor: cancelInfo?.cancelledBy || null,
      detail: detailParts.length > 0 ? detailParts.join(' · ') : null,
      color: 'text-red-600 dark:text-red-400',
      dotColor: 'bg-red-500',
    });
  }

  // Sort chronologically
  events.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

  return (
    <div className="border-t border-slate-200 dark:border-slate-800 pt-2.5">
      <button
        type="button"
        onClick={() => setExpanded(prev => !prev)}
        className="flex items-center justify-between w-full text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 py-1.5 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
      >
        <span className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          Activity History ({events.length} event{events.length !== 1 ? 's' : ''})
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`} />
      </button>
      {expanded && (
        <div className="mt-3">
          {events.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400 italic">No activity recorded yet.</p>
          ) : (
            <div className="relative pl-4">
              {/* Vertical connector line */}
              <div className="absolute left-[7px] top-1.5 bottom-1.5 w-px bg-slate-200 dark:bg-slate-700" />
              <div className="space-y-3">
                {events.map((ev, i) => (
                  <div key={`${ev.label}-${i}`} className="relative flex items-start gap-3">
                    {/* Dot */}
                    <div className={`w-[9px] h-[9px] rounded-full ${ev.dotColor} shrink-0 mt-1.5 -ml-[5px] ring-2 ring-white dark:ring-slate-900 z-10`} />
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{ev.label}</span>
                        {ev.detail && <span className="text-xs text-slate-600 dark:text-slate-300">{ev.detail}</span>}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                        <span className="tabular-nums">{fmtEventTime(ev.time)}</span>
                        {ev.actor && <span>· {ev.actor}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
